import {
    health_activity_label,
    health_tickettype_label,
    health_layout_label,
    health_bucketlist_label,
} from './healthCheckInferLinks';
import Utils from './Utils';

/**
 * Language tag for health-check link labels: lowercase, omitted when it
 * matches the instance default language.
 *
 * @param {string|null|undefined} lang
 * @returns {string} e.g. `en`, or `''` when default / empty
 */
export function health_check_link_lang_label(lang) {
    if (!lang)
        return '';
    const normalized = String(lang).toLowerCase();
    const default_lang = String(Utils.default_lang() || '').toLowerCase();
    if (default_lang && normalized === default_lang)
        return '';
    return normalized;
}

/**
 * Map health check result link descriptors to in-app routes (Kronos).
 * Engine emits semantic `resource` + settings document `id` only; paths live here.
 * For integrations, `id` may be `settingId/integrationKey` (e.g. `default/wp`).
 * Optional `tab` (e.g. tickettype → emails|pdflayouts|windows|pricings) opens the matching form tab.
 *
 * @param {{ resource: string, id: string, tab?: string }} link
 * @returns {string|null} null for `external` (use href directly)
 */
export function pathForHealthCheckLink(link) {
    if (!link?.resource || link.id === undefined || link.id === null)
        return null;

    if (link.resource === 'external')
        return null;

    const enc = (s) => encodeURIComponent(String(s));
    const with_tab = (base, tab) => (tab ? `${base}/${enc(tab)}` : base);

    const settingsTab = {
        settings_retention: 'retention',
        settings_integrations: 'integrations',
        settings_payment: 'payment',
        settings_images: 'images',
        settings_mobile: 'mobile',
        settings_votes: 'votes',
        settings_default_layouts: 'default_layouts',
        settings_eshop: 'eshop',
        settings_customer: 'customer',
    };
    const tab = settingsTab[link.resource];
    if (tab) {
        const raw_id = String(link.id);
        if (tab === 'integrations') {
            const slash = raw_id.indexOf('/');
            if (slash !== -1) {
                const setting_id = enc(raw_id.slice(0, slash));
                const integration_key = enc(raw_id.slice(slash + 1));
                return `/settings/edit/${setting_id}/integrations/${integration_key}`;
            }
        }
        return `/settings/edit/${enc(raw_id)}/${tab}`;
    }

    const id = enc(link.id);

    switch (link.resource) {
        case 'settings':
            return `/settings/edit/${id}`;
        case 'screenings_index':
            return '/screenings';
        case 'cron_tasks_index':
            return '/cron_tasks';
        case 'tickettype':
            return with_tab(`/tickettypes/edit/${id}`, link.tab);
        case 'email_layout':
        case 'pdf_layout': {
            const base = link.resource === 'email_layout'
                ? `/email_layouts/edit/${id}`
                : `/layouts/edit/${id}`;
            const qs = new URLSearchParams();
            if (link.widget)
                qs.set('widget', String(link.widget));
            if (link.lang)
                qs.set('lang', String(link.lang));
            const query = qs.toString();
            return query ? `${base}?${query}` : base;
        }
        case 'cashregister':
            return `/cashregisters/edit/${id}`;
        case 'cashregisters':
            return link.id === 'list' || link.id === 'default'
                ? '/cashregisters'
                : `/cashregisters/edit/${id}`;
        case 'bucketlist':
            return `/bucketlists/edit/${id}`;
        case 'pricinglist':
            return `/pricinglists/edit/${id}`;
        case 'screening':
            return with_tab(`/screenings/edit/${id}`, link.tab);
        case 'cron_task':
            return `/cron_tasks/view/${id}`;
        case 'task':
            return `/tasks/edit/${id}`;
        case 'place':
            return `/places/edit/${id}`;
        case 'salepoint':
            return `/salepoints/edit/${id}`;
        case 'user':
            return `/users/edit/${id}/general`;
        case 'ticketack_user':
            return `/ticketack_users/edit/${id}`;
        case 'editions':
            return link.id === 'list' ? '/editions' : `/editions/edit/${id}`;
        case 'equipment_orders':
            return link.id === 'list' || link.id === 'default'
                ? '/equipment_orders'
                : `/equipment_orders/edit/${id}`;
        case 'activity':
            return `/activities/edit/${id}`;
        default:
            return null;
    }
}

export function isExternalHealthCheckLink(link) {
    if (!link || typeof link.id !== 'string')
        return false;
    if (link.resource === 'wordpress_admin' || link.resource === 'wordpress_post_edit')
        return /^https?:\/\//i.test(link.id);
    return link.resource === 'external' && /^https:\/\//i.test(link.id);
}

/**
 * Split health-check deep-link copy into a clickable open label and an optional
 * fix suffix (issues stay outside the `<a>`).
 *
 * @param {{ resource: string, id: string, issues?: string[], issue_action?: string }} link
 * @param {import('i18next').TFunction} t
 * @param {{ tickettypes?: Array<Object>, activities?: Array<Object>, layouts?: Array<Object>, bucketlists?: Array<Object>, lang?: string }} [context]
 * @returns {{ openLabel: string, fixSuffix: string|null, issues: string[] }}
 */
export function getHealthCheckLinkParts(link, t, context) {
    const empty = { openLabel: '', fixSuffix: null, issues: [] };

    if (!link?.resource)
        return empty;

    if (link.resource === 'wordpress_admin')
        return { openLabel: t('health.link.wordpress_admin'), fixSuffix: null, issues: [] };

    if (link.resource === 'wordpress_post_edit') {
        const page = t(`health.wordpress_page.${link.page || ''}`, {
            defaultValue: link.page || '',
        });
        const lang = health_check_link_lang_label(link.lang);
        const tags = String(link.tags || '')
            .split('|')
            .map((tag) => tag.trim())
            .filter(Boolean)
            .map((tag) => `[${tag}]`)
            .join(' / ');
        return {
            openLabel: lang
                ? t('health.link.wordpress_edit_page_lang', { page, lang })
                : t('health.link.wordpress_edit_page', { page }),
            fixSuffix: tags
                ? t('health.link.wordpress_add_shortcode_suffix', { tags })
                : null,
            issues: [],
        };
    }

    if (link.resource === 'external')
        return { openLabel: t('health.link.external'), fixSuffix: null, issues: [] };

    if (link.resource === 'settings_integrations' && typeof link.id === 'string') {
        const slash = link.id.indexOf('/');
        if (slash !== -1) {
            const integration_key = link.id.slice(slash + 1);
            const name = t(`settings.integrations.${integration_key}.title`, {
                defaultValue: integration_key,
            });
            return {
                openLabel: t('health.link.settings_integration', { name }),
                fixSuffix: null,
                issues: [],
            };
        }
    }

    const issues = Array.isArray(link.issues) ? link.issues.filter(Boolean) : [];
    const has_fix = Boolean(link.issue_action && issues.length);
    const fixSuffix = has_fix
        ? t(`health.link.${link.issue_action}_suffix`, { count: issues.length })
        : null;

    if (link.resource === 'tickettype') {
        const name = health_tickettype_label(link.id, context);
        return {
            openLabel: t('health.link.tickettype', { id: name }),
            fixSuffix: link.issue_action === 'fix_years' ? fixSuffix : null,
            issues: link.issue_action === 'fix_years' ? issues : [],
        };
    }

    if (link.resource === 'activity') {
        return {
            openLabel: t('health.link.activity', { id: health_activity_label(link.id, context) }),
            fixSuffix: null,
            issues: [],
        };
    }

    if (link.resource === 'email_layout' || link.resource === 'pdf_layout') {
        const name = health_layout_label(link.id, context);
        const lang = health_check_link_lang_label(link.lang);
        const action_ok = link.issue_action === 'fix_links' || link.issue_action === 'fix_years';
        return {
            openLabel: lang
                ? t(`health.link.${link.resource}_lang`, { name, lang })
                : t(`health.link.${link.resource}`, { name }),
            fixSuffix: action_ok ? fixSuffix : null,
            issues: action_ok ? issues : [],
        };
    }

    if (link.resource === 'bucketlist') {
        return {
            openLabel: t('health.link.bucketlist', { id: health_bucketlist_label(link.id, context) }),
            fixSuffix: null,
            issues: [],
        };
    }

    let id = link.id;
    if (typeof link.id === 'string' && link.id.length > 8) {
        if (link.resource === 'screening')
            id = link.id.slice(0, 8);
    }

    return {
        openLabel: t(`health.link.${link.resource}`, { id }),
        fixSuffix: null,
        issues: [],
    };
}

/**
 * @param {{ resource: string, id: string, issues?: string[], issue_action?: string }} link
 * @param {import('i18next').TFunction} t
 * @param {{ tickettypes?: Array<Object>, activities?: Array<Object>, layouts?: Array<Object>, bucketlists?: Array<Object>, lang?: string }} [context]
 */
export function getHealthCheckLinkLabel(link, t, context) {
    const { openLabel, fixSuffix, issues } = getHealthCheckLinkParts(link, t, context);
    if (!fixSuffix || !issues.length)
        return openLabel;
    return `${openLabel}${fixSuffix}${issues.join(', ')}`;
}
